/**
 * Student Catchment one-pager export (standalone HTML).
 */

export const EDC_LOGO_PATH = '/images/edc_logo_horizontal.png';
export const EDC_PURPLE = '#673ab7';

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function catchmentOnePagerFilename(schoolName, ext, now = new Date()) {
  const sanitized = String(schoolName || 'school')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase() || 'school';
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${sanitized}_student_catchment_${yyyy}-${mm}-${dd}.${ext}`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function fetchImageAsDataUrl(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to load ${url}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(blob);
  });
}

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to rasterize map overlay'));
    img.src = url;
  });
}

export function elementOffsetInMap(map, el) {
  const mapRect = map.getContainer().getBoundingClientRect();
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left - mapRect.left,
    y: rect.top - mapRect.top,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * Clone a Leaflet overlay SVG so it can be drawn onto a canvas.
 * Pane CSS transforms must be stripped — placement is handled separately.
 */
export function cloneLeafletSvgForCapture(svg) {
  const cloned = svg.cloneNode(true);
  cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  if (!cloned.getAttribute('xmlns:xlink')) {
    cloned.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  }
  const width = svg.clientWidth || svg.getBoundingClientRect().width;
  const height = svg.clientHeight || svg.getBoundingClientRect().height;
  if (width) cloned.setAttribute('width', String(Math.round(width)));
  if (height) cloned.setAttribute('height', String(Math.round(height)));
  cloned.style.transform = 'none';
  cloned.style.left = '0px';
  cloned.style.top = '0px';
  cloned.classList.remove('leaflet-zoom-animated');
  return cloned;
}

async function drawSvgElementOnCanvas(map, ctx, svg, scaleX, scaleY) {
  const box = svg.getBoundingClientRect();
  if (box.width < 1 || box.height < 1) return;
  const cloned = cloneLeafletSvgForCapture(svg);
  const xml = new XMLSerializer().serializeToString(cloned);
  const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = await loadImageFromUrl(url);
    const offset = elementOffsetInMap(map, svg);
    ctx.drawImage(
      img,
      offset.x * scaleX,
      offset.y * scaleY,
      offset.width * scaleX,
      offset.height * scaleY,
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Leaflet 1.x draws GeoJSON, CircleMarker, and Polyline into SVG in overlayPane.
 * leaflet-image only stamps tiles + L.Icon markers, so rings and student dots
 * must be composited separately.
 */
export async function drawLeafletSvgOverlays(map, ctx, canvasWidth, canvasHeight) {
  if (!map || !ctx) return;
  const size = map.getSize();
  if (!size?.x || !size?.y) return;
  const scaleX = canvasWidth / size.x;
  const scaleY = canvasHeight / size.y;
  const overlayPane = typeof map.getPane === 'function' ? map.getPane('overlayPane') : null;
  if (!overlayPane) return;

  for (const svg of overlayPane.querySelectorAll('svg')) {
    try {
      await drawSvgElementOnCanvas(map, ctx, svg, scaleX, scaleY);
    } catch {
      // Skip an overlay that failed to rasterize; tiles/markers can still export.
    }
  }
}

async function drawLeafletMarkerOverlays(map, ctx, canvasWidth, canvasHeight) {
  if (!map || !ctx) return;
  const size = map.getSize();
  if (!size?.x || !size?.y) return;
  const scaleX = canvasWidth / size.x;
  const scaleY = canvasHeight / size.y;
  const markerPane = typeof map.getPane === 'function' ? map.getPane('markerPane') : null;
  if (!markerPane) return;

  const mapRect = map.getContainer().getBoundingClientRect();
  const markerSvgs = [...markerPane.querySelectorAll('svg')];
  // Image icons are already stamped by leaflet-image. Drawing them again
  // duplicates the campus pin. HTML/SVG icons are not image markers.
  for (const svg of markerSvgs) {
    try {
      await drawSvgElementOnCanvas(map, ctx, svg, scaleX, scaleY);
    } catch {
      // Nearby-school DivIcons that fail to rasterize can be omitted.
    }
  }

  const htmlIcons = [...markerPane.querySelectorAll('.leaflet-marker-icon')].filter(
    (el) => el.tagName !== 'IMG' && !el.querySelector('svg') && !el.querySelector('img'),
  );
  htmlIcons.forEach((el) => {
    drawStyledMarkerDiv(ctx, el, mapRect, scaleX, scaleY);
  });
}

function iconPaintTarget(el) {
  const inner = el.firstElementChild;
  if (!inner || typeof window === 'undefined' || !window.getComputedStyle) return el;
  const bg = window.getComputedStyle(inner).backgroundColor;
  if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') return inner;
  return el;
}

function drawStyledMarkerDiv(ctx, el, mapRect, scaleX, scaleY) {
  const rect = el.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return;
  const paint = iconPaintTarget(el);
  const style = window.getComputedStyle(paint);
  const x = (rect.left - mapRect.left) * scaleX;
  const y = (rect.top - mapRect.top) * scaleY;
  const w = rect.width * scaleX;
  const h = rect.height * scaleY;
  const radius = Math.min(w / 2, h / 2, (parseFloat(style.borderRadius) || 0) * scaleX);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fillStyle = style.backgroundColor && style.backgroundColor !== 'rgba(0, 0, 0, 0)'
    ? style.backgroundColor
    : '#64748b';
  ctx.fill();
  const label = (paint.textContent || '').trim();
  if (label) {
    const fontSize = (parseFloat(style.fontSize) || 10) * scaleY;
    ctx.fillStyle = style.color || '#ffffff';
    ctx.font = `700 ${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + w / 2, y + h / 2);
  }
  ctx.restore();
}

/** leaflet-image can only draw <img> icons. DivIcons have no src and crash it. */
export function markerHasImageIcon(layer) {
  const el = layer?._icon;
  return Boolean(el && el.tagName === 'IMG' && el.src);
}

function leafletImageToCanvas(map) {
  return (async () => {
    const leafletImage = (await import('leaflet-image')).default;
    const originalEachLayer = map.eachLayer;
    if (typeof originalEachLayer === 'function') {
      map.eachLayer = function eachLayerWithoutHtmlIcons(fn) {
        return originalEachLayer.call(this, (layer) => {
          if (layer?.getLatLng && layer?.options?.icon && !markerHasImageIcon(layer)) {
            return undefined;
          }
          return fn(layer);
        });
      };
    }
    try {
      return await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Map capture timed out')), 10000);
        leafletImage(map, (err, canvas) => {
          clearTimeout(timeout);
          if (err || !canvas) {
            reject(err || new Error('leaflet-image failed'));
            return;
          }
          resolve(canvas);
        });
      });
    } finally {
      if (typeof originalEachLayer === 'function') map.eachLayer = originalEachLayer;
    }
  })();
}

function canvasLooksBlank(canvas) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  const points = [
    [canvas.width / 2, canvas.height / 2],
    [canvas.width / 4, canvas.height / 4],
    [(canvas.width * 3) / 4, (canvas.height * 3) / 4],
  ];
  try {
    return points.every(([x, y]) => {
      const pixel = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
      const transparent = pixel[3] === 0;
      const white = pixel[0] > 250 && pixel[1] > 250 && pixel[2] > 250;
      return transparent || white;
    });
  } catch {
    return true;
  }
}

function ignoreLeafletChrome(element) {
  return Boolean(
    element?.classList?.contains('leaflet-control-container')
    || element?.classList?.contains('leaflet-popup-pane')
    || element?.closest?.('.leaflet-control-container')
    || element?.closest?.('.leaflet-popup-pane')
  );
}

/**
 * Rasterize the current Leaflet view: tiles, drive-time polygons, student
 * CircleMarkers, then marker icons. Fall back to html2canvas for tiles.
 */
export async function captureLeafletMapImage(map, fallbackElement) {
  let canvas = null;
  let capturePath = 'none';
  if (map) {
    try {
      canvas = await leafletImageToCanvas(map);
      capturePath = 'leaflet-image';
      if (canvasLooksBlank(canvas)) {
        canvas = null;
        capturePath = 'none';
      }
    } catch {
      // CORS or tile decode — try html2canvas
    }
  }

  if (!canvas && fallbackElement) {
    const html2canvas = (await import('html2canvas')).default;
    canvas = await html2canvas(fallbackElement, {
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      scale: 2,
      ignoreElements: ignoreLeafletChrome,
    });
    capturePath = 'html2canvas';
  }

  // html2canvas already includes rings, dots, and the star. Painting them
  // again is what duplicated the campus marker.
  if (canvas && map && capturePath === 'leaflet-image') {
    const ctx = canvas.getContext('2d');
    await drawLeafletSvgOverlays(map, ctx, canvas.width, canvas.height);
    await drawLeafletMarkerOverlays(map, ctx, canvas.width, canvas.height);
  }

  if (!canvas) return null;
  try {
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl && dataUrl.length > 100 ? dataUrl : null;
  } catch {
    return null;
  }
}

function metricCardsHtml(metrics) {
  return (metrics || []).map((m) => `
    <div class="metric">
      <div class="metric-label">${escapeHtml(m.label)}</div>
      <div class="metric-pct">${escapeHtml(String(m.pct))}%</div>
      <div class="metric-count">${escapeHtml(String(m.count))} students</div>
    </div>
  `).join('');
}

function insightCardsHtml(insights) {
  if (!insights) return '';
  const cards = [
    {
      color: insights.dominantColor || EDC_PURPLE,
      headline: insights.dominantHeadline,
      detail: insights.dominantDetail,
    },
    {
      color: insights.medianColor || EDC_PURPLE,
      headline: insights.medianHeadline,
      detail: insights.medianDetail,
    },
    {
      color: '#0891b2',
      headline: insights.geoHeadline,
      detail: insights.geoDetail,
    },
    insights.retentionHeadline
      ? {
          color: '#f59e0b',
          headline: insights.retentionHeadline,
          detail: insights.retentionDetail,
        }
      : null,
  ].filter((c) => c?.headline);

  const cardsHtml = cards.map((c) => `
    <div class="insight" style="border-left-color: ${escapeHtml(c.color)}">
      <div class="insight-headline">${escapeHtml(c.headline)}</div>
      ${c.detail ? `<div class="insight-detail">${escapeHtml(c.detail)}</div>` : ''}
    </div>
  `).join('');

  const advice = insights.adviceHeadline
    ? `
      <div class="advice" style="border-left-color: ${escapeHtml(insights.adviceColor || EDC_PURPLE)}">
        <div class="advice-kicker">Recommendation</div>
        <div class="advice-headline">${escapeHtml(insights.adviceHeadline)}</div>
        ${insights.adviceDetail ? `<div class="advice-detail">${escapeHtml(insights.adviceDetail)}</div>` : ''}
      </div>
    `
    : '';

  return `${advice}<div class="insights">${cardsHtml}</div>`;
}

/**
 * Build a self-contained one-pager HTML document.
 * Logo and map should already be data URLs when possible.
 */
export function buildCatchmentOnePagerHtml({
  schoolName,
  timestamp,
  geocodedCount,
  totalCount,
  insights,
  metrics,
  logoDataUrl,
  mapDataUrl,
} = {}) {
  const title = escapeHtml(schoolName || 'Student Catchment');
  const when = escapeHtml(timestamp || new Date().toLocaleString());
  const subtitle = totalCount != null
    ? `${escapeHtml(geocodedCount ?? 0)} of ${escapeHtml(totalCount)} addresses geocoded`
    : `${escapeHtml(geocodedCount ?? 0)} addresses geocoded`;

  const logo = logoDataUrl
    ? `<img class="logo" src="${logoDataUrl}" alt="Education Data Collective" />`
    : `<div class="logo-fallback">Education Data Collective</div>`;

  const mapBlock = mapDataUrl
    ? `<img class="map" src="${mapDataUrl}" alt="Student catchment map" />`
    : `<div class="map-missing">Map image could not be captured for this export.</div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Student Catchment — ${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: letter; margin: 0.45in; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      color: #1f2937;
      background: #f3f4f6;
    }
    .page {
      width: 816px;
      max-width: 100%;
      margin: 0 auto;
      background: #fff;
      padding: 28px 32px 24px;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      border-bottom: 3px solid ${EDC_PURPLE};
      padding-bottom: 14px;
      margin-bottom: 16px;
    }
    .logo { height: 36px; width: auto; display: block; }
    .logo-fallback { font-weight: 700; color: ${EDC_PURPLE}; font-size: 14px; }
    .header-meta { text-align: right; }
    .kicker { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: ${EDC_PURPLE}; font-weight: 700; }
    .timestamp { font-size: 12px; color: #6b7280; margin-top: 2px; }
    h1 { font-size: 24px; color: ${EDC_PURPLE}; line-height: 1.2; margin-bottom: 4px; }
    .subtitle { font-size: 13px; color: #6b7280; margin-bottom: 16px; }
    .advice {
      border: 1px solid #e5e7eb;
      border-left: 5px solid ${EDC_PURPLE};
      border-radius: 6px;
      padding: 12px 14px;
      margin-bottom: 12px;
      background: #f5f3ff;
    }
    .advice-kicker {
      font-size: 10px;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      font-weight: 700;
      color: ${EDC_PURPLE};
      margin-bottom: 2px;
    }
    .advice-headline { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
    .advice-detail { font-size: 12px; color: #6b7280; line-height: 1.4; }
    .insights { display: flex; gap: 10px; margin-bottom: 12px; align-items: stretch; }
    .insight {
      flex: 1;
      border: 1px solid #e5e7eb;
      border-left: 4px solid ${EDC_PURPLE};
      border-radius: 6px;
      padding: 10px 12px;
    }
    .insight-headline { font-size: 13px; font-weight: 700; margin-bottom: 4px; }
    .insight-detail { font-size: 11px; color: #6b7280; line-height: 1.4; }
    .map { width: 100%; height: auto; border-radius: 6px; border: 1px solid #e5e7eb; display: block; margin: 12px 0; }
    .map-missing {
      margin: 12px 0;
      padding: 36px 16px;
      text-align: center;
      color: #6b7280;
      background: #f9fafb;
      border: 1px dashed #d1d5db;
      border-radius: 6px;
      font-size: 13px;
    }
    .metrics { display: flex; gap: 10px; margin-top: 4px; }
    .metric {
      flex: 1;
      text-align: center;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 10px 8px;
    }
    .metric-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; font-weight: 600; }
    .metric-pct { font-size: 22px; font-weight: 700; color: ${EDC_PURPLE}; line-height: 1.2; margin: 4px 0 2px; }
    .metric-count { font-size: 11px; color: #6b7280; }
    .footer { margin-top: 14px; font-size: 10px; color: #9ca3af; text-align: center; }
    @media print {
      body { background: #fff; }
      .page { width: auto; padding: 0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      ${logo}
      <div class="header-meta">
        <div class="kicker">Student Catchment</div>
        <div class="timestamp">${when}</div>
      </div>
    </div>
    <h1>${title}</h1>
    <div class="subtitle">${subtitle}</div>
    ${insightCardsHtml(insights)}
    ${mapBlock}
    <div class="metrics">${metricCardsHtml(metrics)}</div>
    <div class="footer">Generated by Education Data Collective. Data is subject to change.</div>
  </div>
</body>
</html>`;
}

export function downloadCatchmentOnePagerHtml(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  triggerDownload(blob, filename);
}
